/**
 * Client-side evaluator for the subset of dynamic date expressions used in
 * parameter defaults. Mirrors the semantics of `evalExpression` in shared.
 *
 * Supported: $today, $startOfMonth(N), $endOfMonth(N), $startOfYear, $startOfQuarter.
 * Unsupported expressions are returned as-is (empty string) so the backend resolves them.
 *
 * Contract §1.4: dates toward the backend are in ISO format (YYYY-MM-DD).
 */

function padTwo(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${padTwo(d.getMonth() + 1)}-${padTwo(d.getDate())}`;
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function addMonths(d: Date, n: number): Date {
  const result = new Date(d.getFullYear(), d.getMonth() + n, 1);
  return result;
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3, 1);
}

export function evalDateExpression(expr: string): string | null {
  if (!expr.startsWith('$')) return null;

  const now = new Date();

  if (expr === '$today') return toIso(now);
  if (expr === '$startOfYear') return toIso(startOfYear(now));
  if (expr === '$startOfQuarter') return toIso(startOfQuarter(now));

  const startOfMonthMatch = /^\$startOfMonth\((-?\d+)\)$/.exec(expr);
  if (startOfMonthMatch !== null) {
    const n = Number.parseInt(startOfMonthMatch[1] ?? '0', 10);
    return toIso(startOfMonth(addMonths(now, n)));
  }

  const endOfMonthMatch = /^\$endOfMonth\((-?\d+)\)$/.exec(expr);
  if (endOfMonthMatch !== null) {
    const n = Number.parseInt(endOfMonthMatch[1] ?? '0', 10);
    return toIso(endOfMonth(addMonths(now, n)));
  }

  // Unknown expression — let backend resolve
  return null;
}

/**
 * Resolve a parameter default value to an ISO date string or empty string.
 * If the default is already an ISO/DD-MM-YYYY literal date, normalize it.
 * If it's a dynamic expression, evaluate it.
 * Returns empty string if unresolvable (backend will apply its own default).
 */
export function resolveDefaultDate(defaultValue: string | undefined): string {
  if (defaultValue === undefined) return '';

  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(defaultValue)) return defaultValue;

  // DD-MM-YYYY format
  const ddMmYyyy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(defaultValue);
  if (ddMmYyyy !== null) {
    return `${ddMmYyyy[3]}-${ddMmYyyy[2]}-${ddMmYyyy[1]}`;
  }

  // Dynamic expression
  if (defaultValue.startsWith('$')) {
    return evalDateExpression(defaultValue) ?? '';
  }

  return '';
}
